// Care programmes: each programme (baby vaccinations, pregnancy; IVF and weight loss later) carries
// its own schedule, master call script and master vital-sign ranges. The super admin keeps the
// masters and pushes them to clinics; a clinic's doctor approves before anything is used.
//
// Safety and audit (DPDP Act; built to NHS DSPT / DCB0129 standard):
// - Masters hold no patient data.
// - Every save is a numbered, read-only version with a required change reason (programmes/{id}/versions).
// - Master ranges cannot be pushed until a named doctor's clinical review is recorded for that version.
// - A push never overwrites what a clinic's doctor approved: it arrives as a draft / proposal to approve.
// - Every save, review and push is written to the audit log. The database rules enforce the same.
import { collection, deleteDoc, doc, getDoc, getDocs, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import type { AgentType, Stage, VitalLimit } from '../types';
import { addAudit, type Actor } from './auditService';
import { DEFAULT_LIMITS, VITAL_LIBRARY, type VitalGroup } from './codes';
import { respond } from './respond';
import { draftProblems, loadScript, type ScriptContent } from './scriptService';
import { isReal } from './store';

export type ProgrammeId = 'baby' | 'pregnancy' | 'ivf' | 'weight';

export interface ProgrammeDef {
  id: ProgrammeId;
  status: 'active' | 'coming_soon';
  stage?: Stage;
  agentType?: AgentType;
  /** Shown on coming-soon programmes: the legal / clinical limits that will apply. */
  caution?: 'ivfAdminOnly' | 'weightMeds';
  /** What screens call the people in this programme: "Mothers" (Suraksha Sathi) or "Patients". */
  personTerm: PersonTerm;
}

export type PersonTerm = 'mother' | 'patient';

export const PROGRAMMES: ProgrammeDef[] = [
  { id: 'baby', status: 'active', stage: 'baby', agentType: 'baby', personTerm: 'mother' },
  { id: 'pregnancy', status: 'active', stage: 'pregnancy', agentType: 'pregnancy', personTerm: 'mother' },
  { id: 'ivf', status: 'coming_soon', caution: 'ivfAdminOnly', personTerm: 'patient' },
  { id: 'weight', status: 'coming_soon', caution: 'weightMeds', personTerm: 'patient' },
];

/** The clinic's menu and page titles: "Mothers & babies" when every programme it runs is maternity; otherwise "Patients". */
export function personTermFor(services: Partial<Record<string, boolean>> | undefined): PersonTerm {
  const on = PROGRAMMES.filter((p) => services?.[p.id]);
  return on.length && on.some((p) => p.personTerm === 'patient') ? 'patient' : 'mother';
}

/** In-row wording follows each record's own programme ("Call mother" for baby / pregnancy, "Call patient" for others). */
export function personTermOfStage(stage: string | undefined): PersonTerm {
  return PROGRAMMES.find((p) => p.stage === stage)?.personTerm ?? 'patient';
}

/** Library groups each programme draws its vital ranges from (the whole standard set, kept ready). */
export const PROGRAMME_GROUPS: Record<Stage, VitalGroup[]> = { pregnancy: ['adult', 'pregnancy'], baby: ['baby'] };
export const programmeVitals = (stage: Stage) => VITAL_LIBRARY.filter((v) => PROGRAMME_GROUPS[stage].includes(v.group)).map((v) => v.key);

export interface MasterScript extends ScriptContent {
  version: number; // 0 = Suraksha's built-in starting script, never saved yet
  changeNote: string;
  updatedByName?: string;
  updatedAt?: string;
}

export interface ClinicalReview { name: string; regNo: string; version: number; recordedByName: string; at: string }

export interface MasterThresholds {
  version: number;
  limits: Record<string, VitalLimit>;
  changeNote: string;
  updatedByName?: string;
  updatedAt?: string;
  /** Required before these ranges can be pushed to any clinic. */
  review?: ClinicalReview;
}

export interface ProgrammeData {
  def: ProgrammeDef;
  script?: MasterScript;
  thresholds?: MasterThresholds;
}

export interface VersionEntry { id: string; kind: 'script' | 'thresholds'; version: number; changeNote: string; updatedByName: string; updatedAt: string }

// ---------- Built-in starting points (version 0). Doctors must still review before use. ----------

const BASE_GREETING = 'नमस्ते, क्या मेरी बात {name} जी से हो रही है? मैं {clinic} से, {doctor} की तरफ़ से आशा बोल रही हूँ। मैं एक AI सहायक हूँ।';

const STARTER_SCRIPTS: Partial<Record<ProgrammeId, ScriptContent>> = {
  baby: {
    greetingHi: BASE_GREETING,
    scriptHi: [
      'आपके बच्चे का {dose} का टीका {date} को {clinic} में लगना है।',
      'क्या आप उस दिन बच्चे को लेकर आ पाएँगी?',
      'अगर हाँ: बहुत अच्छा जी। बच्चे का टीकाकरण कार्ड साथ लाना न भूलें।',
      'अगर नहीं या पक्का नहीं: कोई बात नहीं। कृपया क्लिनिक आकर या फ़ोन करके नई तारीख़ तय कर लीजिए। मैंने क्लिनिक के लिए नोट कर लिया है।',
      'टीका समय पर लगवाना बच्चे की सुरक्षा के लिए ज़रूरी है।',
      'धन्यवाद जी, आपका और बच्चे का ध्यान रखिए।',
    ].join('\n'),
    faqs: [
      { q: 'आप कौन बोल रही हैं?', a: 'मैं आशा हूँ, {clinic} की ओर से एक AI सहायक। मैं सिर्फ़ टीके की याद दिलाने के लिए फ़ोन करती हूँ।' },
      { q: 'आपको मेरा नंबर कहाँ से मिला?', a: 'आपने {clinic} में रजिस्ट्रेशन के समय फ़ोन पर याद दिलाने की अनुमति दी थी।' },
      { q: 'क्या साथ लाना है?', a: 'बच्चे का टीकाकरण कार्ड साथ लाइए।' },
      { q: 'टीके का ख़र्च कितना है?', a: 'कृपया अपनी अगली OPD विज़िट पर क्लिनिक में पूछ लीजिए। मैंने क्लिनिक के लिए यह नोट कर लिया है।' },
      { q: 'किसी और दिन आ सकती हूँ?', a: 'कोई बात नहीं। कृपया क्लिनिक आकर या फ़ोन करके नई तारीख़ तय कर लीजिए। मैंने क्लिनिक के लिए नोट कर लिया है।' },
    ],
  },
  pregnancy: {
    greetingHi: BASE_GREETING,
    scriptHi: [
      'आपकी अगली जाँच {date} को {clinic} में है।',
      'क्या आप उस दिन आ पाएँगी?',
      'अगर हाँ: बहुत अच्छा जी। अपना जाँच कार्ड और रिपोर्ट साथ लाइए।',
      'अगर नहीं या पक्का नहीं: कोई बात नहीं। कृपया क्लिनिक आकर या फ़ोन करके नई तारीख़ तय कर लीजिए। मैंने क्लिनिक के लिए नोट कर लिया है।',
      'धन्यवाद जी, अपना ध्यान रखिए।',
    ].join('\n'),
    faqs: [
      { q: 'आप कौन बोल रही हैं?', a: 'मैं आशा हूँ, {clinic} की ओर से एक AI सहायक। मैं सिर्फ़ जाँच की याद दिलाने के लिए फ़ोन करती हूँ।' },
      { q: 'क्या साथ लाना है?', a: 'अपना जाँच कार्ड और पिछली रिपोर्ट साथ लाइए।' },
      { q: 'जाँच का ख़र्च कितना है?', a: 'कृपया अपनी अगली OPD विज़िट पर क्लिनिक में पूछ लीजिए। मैंने क्लिनिक के लिए यह नोट कर लिया है।' },
    ],
  },
};

const starterThresholds = (stage: Stage): Record<string, VitalLimit> =>
  Object.fromEntries(programmeVitals(stage).map((k) => [k, { ...DEFAULT_LIMITS[k] }]));

function withDefaults(def: ProgrammeDef, stored?: { script?: MasterScript; thresholds?: MasterThresholds }): ProgrammeData {
  if (def.status !== 'active' || !def.stage) return { def };
  const starter = STARTER_SCRIPTS[def.id];
  return {
    def,
    script: stored?.script ?? (starter ? { ...starter, faqs: starter.faqs.map((f) => ({ ...f })), version: 0, changeNote: '' } : undefined),
    thresholds: stored?.thresholds
      ? { ...stored.thresholds, limits: { ...starterThresholds(def.stage), ...stored.thresholds.limits } }
      : { version: 0, limits: starterThresholds(def.stage), changeNote: '' },
  };
}

// Demo mode: in-memory only (resets on reload).
const demo: Record<string, { script?: MasterScript; thresholds?: MasterThresholds; versions: VersionEntry[] }> = {};
const demoEntry = (id: string) => (demo[id] ??= { versions: [] });

const pref = (id: ProgrammeId) => doc(db!, 'programmes', id);

export async function getProgrammes(): Promise<ProgrammeData[]> {
  if (!isReal()) return respond(PROGRAMMES.map((d) => withDefaults(d, demo[d.id])));
  return Promise.all(PROGRAMMES.map(async (d) => {
    if (d.status !== 'active') return { def: d };
    const s = await getDoc(pref(d.id));
    return withDefaults(d, s.exists() ? (s.data() as { script?: MasterScript; thresholds?: MasterThresholds }) : undefined);
  }));
}

export async function getProgrammeVersions(id: ProgrammeId): Promise<VersionEntry[]> {
  if (!isReal()) return respond([...demoEntry(id).versions].reverse());
  const s = await getDocs(collection(db!, 'programmes', id, 'versions'));
  return s.docs.map((d) => ({ ...(d.data() as VersionEntry), id: d.id })).sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

const mustBeSuper = (actor?: Actor): Actor => {
  if (actor?.role !== 'super_admin') throw new Error('Super admin only');
  return actor;
};

async function writeVersion(id: ProgrammeId, entry: Omit<VersionEntry, 'id'>, content: object) {
  const vid = `${entry.kind}-v${entry.version}`;
  if (!isReal()) { demoEntry(id).versions.push({ ...entry, id: vid }); return; }
  await setDoc(doc(db!, 'programmes', id, 'versions', vid), JSON.parse(JSON.stringify({ ...entry, content })));
}

export async function saveMasterScript(id: ProgrammeId, content: ScriptContent, changeNote: string, actor?: Actor): Promise<number> {
  const a = mustBeSuper(actor);
  const problems = draftProblems(content);
  if (problems.length) throw new Error(problems[0]);
  if (!changeNote.trim()) throw new Error('reasonRequired');
  const current = (await getProgrammes()).find((p) => p.def.id === id)?.script;
  const at = new Date().toISOString();
  const script: MasterScript = {
    greetingHi: content.greetingHi.trim(), scriptHi: content.scriptHi.trim(), faqs: content.faqs.map((f) => ({ q: f.q.trim(), a: f.a.trim() })),
    version: (current?.version ?? 0) + 1, changeNote: changeNote.trim(), updatedByName: a.name, updatedAt: at,
  };
  if (isReal()) await setDoc(pref(id), { script }, { merge: true });
  else demoEntry(id).script = script;
  await writeVersion(id, { kind: 'script', version: script.version, changeNote: script.changeNote, updatedByName: a.name, updatedAt: at }, script);
  addAudit(a, 'master.script.saved', `programme ${id} v${script.version}`, undefined, script.changeNote);
  return script.version;
}

export async function saveMasterThresholds(id: ProgrammeId, limits: Record<string, VitalLimit>, changeNote: string, actor?: Actor): Promise<number> {
  const a = mustBeSuper(actor);
  if (!changeNote.trim()) throw new Error('reasonRequired');
  const bad = Object.values(limits).some((l) => {
    const order = [l.redLow, l.amberLow, l.amberHigh, l.redHigh].filter((n): n is number => n !== undefined);
    return order.some((n, i) => Number.isNaN(n) || (i > 0 && n < order[i - 1]));
  });
  if (bad) throw new Error('rangeOrder');
  const current = (await getProgrammes()).find((p) => p.def.id === id)?.thresholds;
  const at = new Date().toISOString();
  // A new version always needs a fresh clinical review (the old review does not carry over).
  const thresholds: MasterThresholds = { version: (current?.version ?? 0) + 1, limits: JSON.parse(JSON.stringify(limits)), changeNote: changeNote.trim(), updatedByName: a.name, updatedAt: at };
  if (isReal()) await setDoc(pref(id), { thresholds }, { merge: true });
  else demoEntry(id).thresholds = thresholds;
  await writeVersion(id, { kind: 'thresholds', version: thresholds.version, changeNote: thresholds.changeNote, updatedByName: a.name, updatedAt: at }, thresholds);
  addAudit(a, 'master.thresholds.saved', `programme ${id} v${thresholds.version}`, undefined, thresholds.changeNote);
  return thresholds.version;
}

/** Records that a named, registered doctor reviewed this exact version of the master ranges. */
export async function recordThresholdReview(id: ProgrammeId, name: string, regNo: string, actor?: Actor): Promise<void> {
  const a = mustBeSuper(actor);
  const t = (await getProgrammes()).find((p) => p.def.id === id)?.thresholds;
  if (!t?.version) throw new Error('saveFirst');
  if (!name.trim() || !regNo.trim()) throw new Error('reviewerRequired');
  const review: ClinicalReview = { name: name.trim(), regNo: regNo.trim(), version: t.version, recordedByName: a.name, at: new Date().toISOString() };
  if (isReal()) await updateDoc(pref(id), { 'thresholds.review': review });
  else demoEntry(id).thresholds = { ...t, review };
  addAudit(a, 'master.thresholds.reviewed', `programme ${id} v${t.version}`, undefined, `${review.name} (${review.regNo})`);
}

export const reviewCurrent = (t?: MasterThresholds) => !!t?.review && t.review.version === t.version;

// ---------- Push to clinics ----------

export interface PushResult { pushed: string[]; upToDate: string[]; skipped: string[] }

const sameContent = (a?: ScriptContent, b?: ScriptContent) =>
  !!a && !!b && a.greetingHi === b.greetingHi && a.scriptHi === b.scriptHi && JSON.stringify(a.faqs) === JSON.stringify(b.faqs);

/** Sends the master script to clinics as a draft awaiting their doctor. Approved versions keep calling meanwhile. */
export async function pushScript(id: ProgrammeId, facilityIds: string[], actor?: Actor): Promise<PushResult> {
  const a = mustBeSuper(actor);
  const p = (await getProgrammes()).find((x) => x.def.id === id);
  const type = p?.def.agentType;
  if (!p?.script || !type) throw new Error('notActive');
  if (!p.script.version) throw new Error('saveFirst');
  const res: PushResult = { pushed: [], upToDate: [], skipped: [] };
  for (const fid of facilityIds) {
    if (!isReal()) { res.pushed.push(fid); continue; }
    const cur = await loadScript(fid, type);
    if (sameContent(cur?.approved, p.script) || (cur?.status === 'awaiting_signoff' && sameContent(cur.draft, p.script))) { res.upToDate.push(fid); continue; }
    const patch = {
      draft: { greetingHi: p.script.greetingHi, scriptHi: p.script.scriptHi, faqs: p.script.faqs },
      status: 'awaiting_signoff', editedBy: a.id, editedByName: `${a.name} (Suraksha master v${p.script.version})`, editedAt: new Date().toISOString(),
      pushedFrom: { programme: id, version: p.script.version },
    };
    const ref = doc(db!, 'facilities', fid, 'scripts', type);
    if (cur) await updateDoc(ref, patch);
    else await setDoc(ref, { agentType: type, comments: [], ...patch });
    res.pushed.push(fid);
    addAudit(a, 'master.script.pushed', `${fid} · ${id} v${p.script.version}`, fid);
  }
  return res;
}

export interface ThresholdProposal {
  programme: ProgrammeId;
  version: number;
  limits: Record<string, VitalLimit>;
  reviewedBy: string;
  pushedBy: string;
  pushedByName: string;
  pushedAt: string;
}

/** Sends reviewed master ranges to clinics as a proposal; the clinic's doctor accepts (or edits) them. */
export async function pushThresholds(id: ProgrammeId, facilityIds: string[], actor?: Actor): Promise<PushResult> {
  const a = mustBeSuper(actor);
  const p = (await getProgrammes()).find((x) => x.def.id === id);
  if (!p?.thresholds?.version) throw new Error('saveFirst');
  if (!reviewCurrent(p.thresholds)) throw new Error('reviewRequired');
  const res: PushResult = { pushed: [], upToDate: [], skipped: [] };
  const proposal: ThresholdProposal = {
    programme: id, version: p.thresholds.version, limits: p.thresholds.limits,
    reviewedBy: `${p.thresholds.review!.name} (${p.thresholds.review!.regNo})`,
    pushedBy: a.id, pushedByName: a.name, pushedAt: new Date().toISOString(),
  };
  for (const fid of facilityIds) {
    if (!isReal()) { res.pushed.push(fid); continue; }
    const src = (await getDoc(doc(db!, 'facilities', fid, 'settings', 'vitalLimits'))).data()?.sources as Record<string, number> | undefined;
    if (src?.[id] === p.thresholds.version) { res.upToDate.push(fid); continue; }
    await setDoc(doc(db!, 'facilities', fid, 'settings', `vitalLimitsProposed-${id}`), JSON.parse(JSON.stringify(proposal)));
    res.pushed.push(fid);
    addAudit(a, 'master.thresholds.pushed', `${fid} · ${id} v${proposal.version}`, fid);
  }
  return res;
}

/** Called when a new clinic is created: it starts with the ready-made masters for its programmes. */
export async function seedNewClinic(fid: string, services: Partial<Record<AgentType, boolean>>, actor?: Actor): Promise<void> {
  if (actor?.role !== 'super_admin') return;
  for (const p of PROGRAMMES.filter((x) => x.status === 'active' && x.agentType && services[x.agentType])) {
    await pushScript(p.id, [fid], actor).catch(() => undefined); // not saved yet = nothing to push
    await pushThresholds(p.id, [fid], actor).catch(() => undefined); // not reviewed yet = nothing to push
  }
}

// ---------- Clinic side: proposals waiting for the doctor ----------

export async function getThresholdProposals(fid: string): Promise<ThresholdProposal[]> {
  if (!isReal()) return respond([]);
  const out: ThresholdProposal[] = [];
  for (const p of PROGRAMMES.filter((x) => x.status === 'active')) {
    const s = await getDoc(doc(db!, 'facilities', fid, 'settings', `vitalLimitsProposed-${p.id}`));
    if (s.exists()) out.push(s.data() as ThresholdProposal);
  }
  return out;
}

/** Doctor accepts the proposed ranges: they replace this programme's ranges at the clinic. */
export async function acceptThresholds(fid: string, proposal: ThresholdProposal, actor?: Actor): Promise<void> {
  if (actor?.role !== 'doctor') throw new Error('Only doctors can accept');
  const ref = doc(db!, 'facilities', fid, 'settings', 'vitalLimits');
  const cur = (await getDoc(ref)).data() ?? {};
  const limits = { ...(cur.limits ?? JSON.parse(JSON.stringify(DEFAULT_LIMITS))), ...proposal.limits };
  const sources = { ...(cur.sources ?? {}), [proposal.programme]: proposal.version };
  await setDoc(ref, JSON.parse(JSON.stringify({ limits, sources, updatedAt: new Date().toISOString(), updatedBy: actor.id })));
  await deleteDoc(doc(db!, 'facilities', fid, 'settings', `vitalLimitsProposed-${proposal.programme}`));
  addAudit(actor, 'thresholds.accepted', `${fid} · ${proposal.programme} v${proposal.version}`, fid, proposal.reviewedBy);
}

export async function dismissThresholds(fid: string, proposal: ThresholdProposal, actor?: Actor): Promise<void> {
  if (actor?.role !== 'doctor') throw new Error('Only doctors can decide');
  await deleteDoc(doc(db!, 'facilities', fid, 'settings', `vitalLimitsProposed-${proposal.programme}`));
  addAudit(actor, 'thresholds.declined', `${fid} · ${proposal.programme} v${proposal.version}`, fid);
}

/** Super admin overview: where each clinic stands for this programme (no patient data). */
export async function getPushStatus(id: ProgrammeId, facilityIds: string[]): Promise<Record<string, { script: string; thresholds: string }>> {
  const p = (await getProgrammes()).find((x) => x.def.id === id);
  const out: Record<string, { script: string; thresholds: string }> = {};
  for (const fid of facilityIds) {
    if (!isReal() || !p?.def.agentType) { out[fid] = { script: 'none', thresholds: 'none' }; continue; }
    const s = await loadScript(fid, p.def.agentType);
    const pushed = (s as { pushedFrom?: { version: number } } | undefined)?.pushedFrom?.version;
    const script = !s ? 'none' : s.status === 'approved' ? (sameContent(s.approved, p.script) ? 'approvedCurrent' : 'approvedOther') : pushed ? 'awaitingDoctor' : 'clinicDraft';
    const lim = (await getDoc(doc(db!, 'facilities', fid, 'settings', 'vitalLimits'))).data()?.sources as Record<string, number> | undefined;
    const prop = (await getDoc(doc(db!, 'facilities', fid, 'settings', `vitalLimitsProposed-${id}`))).exists();
    const thresholds = prop ? 'awaitingDoctor' : lim?.[id] ? (lim[id] === p.thresholds?.version ? 'approvedCurrent' : 'approvedOther') : 'none';
    out[fid] = { script, thresholds };
  }
  return out;
}
